import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { modeUIContext, setLanguage, selectLanguage } from '@moderepo/mode-ui-state';
import { Agent } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectLoggedInAgent, selectProjectById, updateAgentPersonalProfile } from '@moderepo/mode-data-state-ritz';
import { LanguageSelector, SelectInputOption, getLanguageByCode, Language } from '@moderepo/mode-app-components';
import * as Constants from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Utils from '../../utils/utils';


interface LanguageSelectorContainerProps {
    readonly className?: string;
    readonly minimal?: boolean;     // Whether or not to show the minimal UI. If true, we will not show the language name, only the dropdown arrow.
    readonly projectId: number | undefined;
}


/**
 * This is the Language dropdown selector component which can be used any where in the app. To use the component, add this to the
 * template <LanguageSelector />
 */
export const LanguageSelectorContainer: React.FC<LanguageSelectorContainerProps> = (props: LanguageSelectorContainerProps) => {
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = props.projectId ? selectProjectById(dataState, props.projectId) : undefined;
    const { trans } = useRitzTranslation(project);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;

    const selectedLanguage = getLanguageByCode(
        Constants.SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || Constants.DEFAULT_LANGUAGE;

    // Local state of the selected language
    const [currentLanguage, setCurrentLanguage] = useState<Language>(selectedLanguage);


    // Create an Array of Language options to be passed to th LanguageSelectorComponent
    const languageOptions = useMemo(() => {
        return Constants.SUPPORTED_LANGUAGES.map((lang: Language): SelectInputOption<Language> => {
            return {
                label   : trans(lang.name),
                value   : lang,
                selected: currentLanguage === lang,
            };
        });
    }, [currentLanguage, trans]);


    // this useEffect is used for listening to project and logged in agent change and update the language
    useEffect(() => {
        if (loggedInAgent) {
            // if user is logged in then use the language based on the user preference or project preference
            const preferredLanguage = Utils.getPreferredLanguage(project, loggedInAgent, Constants.DEFAULT_LANGUAGE);
            setCurrentLanguage(preferredLanguage);
        } else {
            setCurrentLanguage(selectedLanguage);
        }
    }, [loggedInAgent, project, currentLanguage, selectedLanguage]);


    /**
     * On Language changed event from the LanguageSelectorComponent
     */
    const onLanguageChanged = useCallback(async (event: React.MouseEvent, lang: Language) => {
        if (lang.code !== currentLanguage.code) {

            // If the user is logged in, update the user's profile.
            if (loggedInAgent) {
                const preferences = loggedInAgent.preferences ? {
                    ...loggedInAgent.preferences,
                    locale: lang.locale,
                } : {
                    locale: lang.locale,
                };

                try {
                    await dataDispatch(updateAgentPersonalProfile(loggedInAgent.id, { preferences }));
                } catch (error) {
                    // ignore error
                }
            } else {
                uiDispatch(setLanguage(lang.code));
            }
        }
    }, [dataDispatch, uiDispatch, loggedInAgent, currentLanguage.code]);


    return (
        <LanguageSelector
            className={props.className}
            minimal={props.minimal}
            options={languageOptions}
            onChange={onLanguageChanged}
        />
    );
};
